import { useRouter } from 'expo-router';
import type { ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';
import { StyleSheet, type UnistylesThemes } from 'react-native-unistyles';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  resetWelcomeInviteDraft,
  resetWelcomeTeamDraft,
  type WelcomeFormValues,
} from '@/layouts/Welcome/form';

type TeamSetupTone = 'info' | 'success';
type WelcomeTheme = UnistylesThemes['dark'];

const TEAM_SETUP_OPTIONS: Array<{
  icon: ComponentProps<typeof Icon>['name'];
  onPressPath: '/welcome/team/create' | '/welcome/join';
  subtitle: string;
  tone: TeamSetupTone;
  title: string;
}> = [
  {
    icon: 'people-circle-outline',
    onPressPath: '/welcome/team/create',
    title: 'Create a team',
    subtitle: 'Set up your team profile, sport, and invite link.',
    tone: 'success',
  },
  {
    icon: 'log-in-outline',
    onPressPath: '/welcome/join',
    title: 'Join a team instead',
    subtitle: 'Enter a code for a team someone already created.',
    tone: 'info',
  },
];

export default function WelcomeTeamManager() {
  const { navigate } = useRouter();
  const { setValue } = useFormContext<WelcomeFormValues>();

  const onPressOption = (
    nextPath: '/welcome/team/create' | '/welcome/join',
  ) => {
    if (nextPath === '/welcome/team/create') {
      resetWelcomeInviteDraft(setValue);
      resetWelcomeTeamDraft(setValue);
    } else {
      resetWelcomeTeamDraft(setValue);
      resetWelcomeInviteDraft(setValue);
    }

    navigate(nextPath);
  };

  return (
    <WelcomeStepFrame
      title="How are you setting up your team?"
      subtitle="Start fresh, or enter a code for one someone already created."
    >
      <WelcomeStepScrollContent>
        <Box w="full" space="md">
          {TEAM_SETUP_OPTIONS.map((option) => (
            <Pressable
              accessibilityRole="button"
              key={option.title}
              onPress={() => onPressOption(option.onPressPath)}
              styleOverride={({ pressed }) =>
                styles.option(option.tone, pressed)
              }
            >
              <Box direction="horizontal" alignItems="center" space="md">
                <Box center style={styles.iconBadge}>
                  <Icon name={option.icon} size="3xl" variant={option.tone} />
                </Box>
                <Box flex space="xs">
                  <Text flexShrink={1} size="lg" bold>
                    {option.title}
                  </Text>
                  <Text size="sm" variant="typographySecondary">
                    {option.subtitle}
                  </Text>
                </Box>
                <Icon
                  name="chevron-forward"
                  size="sm"
                  variant="typographySecondary"
                />
              </Box>
            </Pressable>
          ))}
        </Box>
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}

function getToneColor(theme: WelcomeTheme, tone: TeamSetupTone) {
  switch (tone) {
    case 'success':
      return theme.colors.success;
    default:
      return theme.colors.info;
  }
}

const styles = StyleSheet.create((theme) => ({
  iconBadge: {
    alignItems: 'center',
    height: theme.size(9),
    justifyContent: 'center',
    width: theme.size(9),
    _web: {
      _hover: {
        opacity: 0.9,
      },
    },
  },
  option: (tone: TeamSetupTone, active: boolean) => ({
    backgroundColor: active
      ? theme.colors.surfaceSecondary
      : theme.colors.surface,
    borderColor: active ? getToneColor(theme, tone) : theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    padding: theme.space('lg'),
    width: '100%',
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
        borderColor: getToneColor(theme, tone),
      },
    },
  }),
}));
